import { z } from 'zod';

/** Primary page key where asset visibility is persisted (roleAuthorization[].page). */
export const ASSET_INVENTORY_PAGE_KEY = 'assetInventoryAssets';

/**
 * Pages whose visibility should follow the same asset row-level rules as Varlıklar.
 * Effective policy merges across assigned roles (OR of party role keys; unrestricted wins).
 */
export const ASSET_VISIBILITY_SCOPE_PAGE_KEYS = Object.freeze([
    ASSET_INVENTORY_PAGE_KEY,
    'assetInventoryMaintenance',
    'assetInventoryLicenseManagement',
    'assetInventoryDisposalManagement',
    'assetInventoryLifecycleEvents',
]);

// LEGACY-BYPASS: LB-20260914-06 — legacy visibility scope values kept for read-time normalization of old rows
/** @deprecated Legacy DB field; removed from API/UI — read-time normalization only */
export const LEGACY_ASSET_VISIBILITY_SCOPES = Object.freeze(['own', 'unit', 'unit_subtree', 'all']);

export const AssetPartyRoleKeysSchema = z
    .array(z.string().min(1).max(120))
    .max(40)
    .optional();

/**
 * When true (default if omitted): no party-role row filter for assets.
 * When false: `assetPartyRoleKeys` must list PartyTemplate roleKeys the user must match on PartyInstance.
 */
export const AssetInventoryVisibilityUnrestrictedSchema = z.boolean().optional();

/** When true with restricted visibility: unit-type party matches include user's unit and all descendant units (same tree as Unit.parent). */
export const AssetInventoryIncludeDescendantUnitAssetsSchema = z.boolean().optional();

export const RoleAuthorizationAssetVisibilitySchema = z.object({
    assetInventoryVisibilityUnrestricted: AssetInventoryVisibilityUnrestrictedSchema,
    assetPartyRoleKeys: AssetPartyRoleKeysSchema,
    assetInventoryIncludeDescendantUnitAssets: AssetInventoryIncludeDescendantUnitAssetsSchema,
});

/**
 * Normalize legacy `assetVisibilityScope` into new fields (in-memory; strip legacy when persisting from UI).
 * @param {object} row
 * @returns {object}
 */
export function normalizeLegacyAssetAuthorizationRow(row) {
    if (!row?.page || !ASSET_VISIBILITY_SCOPE_PAGE_KEYS.includes(row.page)) {
        return row;
    }
    const base = { ...row };
    delete base.assetVisibilityScope;

    const keys = Array.isArray(row.assetPartyRoleKeys)
        ? [...new Set(row.assetPartyRoleKeys.map(String).filter(Boolean))]
        : [];
    const ur = row.assetInventoryVisibilityUnrestricted;
    const includeFromRow = row.assetInventoryIncludeDescendantUnitAssets === true;
    const restrictedPartyKeys = ['OWNER', 'CUSTODIAN', 'USER'];

    if (ur === true) {
        const { assetInventoryIncludeDescendantUnitAssets: _i, ...rest } = {
            ...base,
            assetInventoryVisibilityUnrestricted: true,
            assetPartyRoleKeys: [],
        };
        return rest;
    }
    if (ur === false) {
        return {
            ...base,
            assetInventoryVisibilityUnrestricted: false,
            assetPartyRoleKeys: keys,
            ...(includeFromRow ? { assetInventoryIncludeDescendantUnitAssets: true } : {}),
        };
    }
    if (keys.length > 0) {
        return {
            ...base,
            assetInventoryVisibilityUnrestricted: false,
            assetPartyRoleKeys: keys,
            ...(includeFromRow ? { assetInventoryIncludeDescendantUnitAssets: true } : {}),
        };
    }

    const legacy = row.assetVisibilityScope;
    if (!legacy || legacy === 'all') {
        const { assetInventoryIncludeDescendantUnitAssets: _i, ...rest } = {
            ...base,
            assetInventoryVisibilityUnrestricted: true,
            assetPartyRoleKeys: [],
        };
        return rest;
    }
    if (legacy === 'own') {
        return {
            ...base,
            assetInventoryVisibilityUnrestricted: false,
            assetPartyRoleKeys: restrictedPartyKeys,
        };
    }
    if (legacy === 'unit') {
        return {
            ...base,
            assetInventoryVisibilityUnrestricted: false,
            assetPartyRoleKeys: restrictedPartyKeys,
        };
    }
    if (legacy === 'unit_subtree') {
        return {
            ...base,
            assetInventoryVisibilityUnrestricted: false,
            assetPartyRoleKeys: restrictedPartyKeys,
            assetInventoryIncludeDescendantUnitAssets: true,
        };
    }
    const { assetInventoryIncludeDescendantUnitAssets: _i2, ...rest2 } = {
        ...base,
        assetInventoryVisibilityUnrestricted: true,
        assetPartyRoleKeys: [],
    };
    return rest2;
}
